import { useTranslation } from 'react-i18next';
import type { CurrencyInfo } from '@/lib/shared-types';
import { Select } from '@/components/ui/field';

interface Props {
  /** Currency registry id, or `''` for the inherit / unset option. */
  value: string;
  onChange: (currencyId: string) => void;
  currencies: CurrencyInfo[];
  /** Offer an empty option with this label. Omit and the field is required. */
  inheritLabel?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * The one currency picker for the Settings screens.
 *
 * Fed by `GET /currencies` through `useCurrency()`, so a tenant can only choose
 * a currency the platform actually defines — never type a code the registry has
 * never heard of, which is what `Branch.currency` used to allow.
 *
 * The selected value is the registry **id**, because that is what
 * `Tenant.baseCurrencyId` / `Branch.currencyId` accept and what the server
 * treats as the authority behind the denormalized code.
 */
export default function CurrencySelect({
  value,
  onChange,
  currencies,
  inheritLabel,
  disabled,
  'aria-label': ariaLabel,
}: Props) {
  const { t } = useTranslation();

  // A branch may point at a currency that has since been retired. It is not in
  // the active list, so without this the select would silently snap to the
  // first option and a save would change the branch's currency by accident.
  const missing = value && !currencies.some((currency) => currency.id === value);

  return (
    <Select
      value={value}
      disabled={disabled}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
    >
      {inheritLabel !== undefined && <option value="">{inheritLabel}</option>}
      {missing && <option value={value}>{t('currency.unknownCurrency')}</option>}
      {currencies.map((currency) => (
        <option key={currency.id ?? currency.code} value={currency.id ?? ''}>
          {t('currency.settings.currencyOf', { code: currency.code, name: currency.name })}
        </option>
      ))}
    </Select>
  );
}
